/**
 * Intro Screen Component
 *
 * Initial screen asking user if they want to connect insurance
 */

"use client";

import { motion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
} from "../insurance-connector-provider";

export function IntroScreen() {
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const t = useTranslations("InsuranceConnector");

  const overrides = context.contentOverrides?.introScreenContent;
  const defaultTitle = `${context.passedEligibility ? t("YoureEligible") : ""}${t("WantToSaveTime", { orgName: context.orgName })}`;
  const titleText = overrides?.titleText ?? defaultTitle;
  const subtitleLines = overrides?.subtitleText ?? [t("ConnectInsuranceBlurb")];

  return (
    <div className="p-0 md:p-6 flex flex-col justify-between h-full">
      <div className="space-y-6 md:space-y-[48px]">
        <div className="space-y-4 md:space-y-[30px]">
          <h2 className="text-[32px] md:text-[48px] leading-[110%] tracking-[-0.02em] font-semibold">
            {titleText}
          </h2>
          <div className="space-y-2">
            {subtitleLines.map((line) => (
              <p
                key={line}
                className="text-[18px] md:text-[24px] leading-[120%] tracking-[-0.02em] font-normal"
              >
                {line}
              </p>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex items-center flex-col-reverse md:flex-row gap-3 md:gap-6">
            <motion.button
              type="button"
              onClick={() => send({ type: "NOT_NOW" })}
              className="rounded-full font-semibold px-6 py-5 border border-black w-full md:w-auto"
              style={{
                whiteSpace: "nowrap",
              }}
              initial={{
                backgroundColor: "transparent",
                color: "#000000",
              }}
              whileHover={{
                backgroundColor: "#000000",
                color: "#ffffff",
              }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
            >
              {t("NotRightNow")}
            </motion.button>
            <motion.button
              type="button"
              onClick={() => send({ type: "YES_CONNECT" })}
              className="rounded-full w-full font-semibold text-white px-6 py-5"
              initial={{
                backgroundColor: "#000000",
              }}
              whileHover={{
                backgroundColor: "#00A3FF",
              }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
            >
              {t("YesConnectInsurance")}
            </motion.button>
          </div>
          {context.containsEPSorWorkersComp && (
            <div className="w-full flex flex-row justify-center md:justify-start">
              <motion.button
                type="button"
                className="flex items-center gap-1 w-fit"
                onClick={() => send({ type: "EMPLOYER_SENT" })}
                whileHover="hover"
                initial="initial"
              >
                <span className="text-sm font-medium">
                  {t("EmployerSentYou")}
                </span>
                <motion.div
                  variants={{
                    initial: { x: 0 },
                    hover: { x: 4 },
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                >
                  <ArrowRightIcon className="w-4 h-4" />
                </motion.div>
              </motion.button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full flex flex-row justify-center md:justify-start mt-6">
        <img
          src="https://rsup2.b-cdn.net/poweredbysuperscript.svg"
          alt={t("PoweredBySuperscript")}
          width={200}
          height={200}
        />
      </div>
    </div>
  );
}
